import { useState, useEffect } from 'react';
import {
  Users, Search, ShieldCheck, ShieldOff, Lock, Trash2,
  UserPlus, Ban, CheckCircle2, Crown, Loader2, KeyRound,
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [createData, setCreateData] = useState({ email: '', name: '', password: '' });
  const [creating, setCreating] = useState(false);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');

  const [blockOpen, setBlockOpen] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockReason, setBlockReason] = useState('');

  const isOwner = !!currentUser?.is_owner;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (search.trim()) params.append('search', search.trim());
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data.items || []);
    } catch (e) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [roleFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const handleCreateAdmin = async () => {
    if (!createData.email || !createData.name || !createData.password) {
      toast.error('Preencha todos os campos');
      return;
    }
    if (createData.password.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }
    setCreating(true);
    try {
      await api.post('/admin/users/create-admin', createData);
      toast.success('Administrador criado!');
      setCreateOpen(false);
      setCreateData({ email: '', name: '', password: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar admin');
    } finally {
      setCreating(false);
    }
  };

  const handlePromote = async (u) => {
    if (!window.confirm(`Promover ${u.email} a administrador?`)) return;
    try {
      await api.put(`/admin/users/${u.id}/promote`);
      toast.success(`${u.email} promovido(a)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const handleDemote = async (u) => {
    if (!window.confirm(`Rebaixar ${u.email} a cliente?`)) return;
    try {
      await api.put(`/admin/users/${u.id}/demote`);
      toast.success(`${u.email} rebaixado(a)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const handleUnblock = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/unblock`);
      toast.success(`${u.email} desbloqueado(a)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const handleBlock = async () => {
    try {
      await api.put(`/admin/users/${blockTarget.id}/block`, { reason: blockReason });
      toast.success(`${blockTarget.email} bloqueado(a)`);
      setBlockOpen(false);
      setBlockReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const handleReset = async () => {
    if (resetPassword.length < 6) {
      toast.error('Senha mínimo 6 caracteres');
      return;
    }
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: resetPassword });
      toast.success(`Senha de ${resetTarget.email} redefinida`);
      setResetOpen(false);
      setResetPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`EXCLUIR DEFINITIVAMENTE ${u.email}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.email} excluído(a)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  return (
    <AdminLayout title="Gerenciar Usuários">
      <div className="space-y-6">
        {/* Filters + Create */}
        <Card className="rounded-2xl">
          <CardContent className="pt-6 flex flex-col lg:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex gap-2 flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nome ou e-mail..."
                  className="pl-9"
                  data-testid="users-search-input"
                />
              </div>
              <Button type="submit" variant="outline" data-testid="users-search-button">Buscar</Button>
            </form>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-48" data-testid="users-role-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="admin">Administradores</SelectItem>
                <SelectItem value="customer">Clientes</SelectItem>
              </SelectContent>
            </Select>
            {isOwner && (
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-admin-button">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Novo Admin
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Criar novo administrador</DialogTitle>
                    <DialogDescription>
                      Apenas o proprietário pode criar administradores. Use uma senha forte.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label>Nome</Label>
                      <Input
                        value={createData.name}
                        onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                        data-testid="create-admin-name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>E-mail</Label>
                      <Input
                        type="email"
                        value={createData.email}
                        onChange={(e) => setCreateData({ ...createData, email: e.target.value })}
                        data-testid="create-admin-email"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Senha (mín. 6 caracteres)</Label>
                      <Input
                        type="password"
                        value={createData.password}
                        onChange={(e) => setCreateData({ ...createData, password: e.target.value })}
                        data-testid="create-admin-password"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
                    <Button onClick={handleCreateAdmin} disabled={creating} data-testid="create-admin-submit">
                      {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Users className="w-5 h-5" />
              {users.length} usuário{users.length !== 1 ? 's' : ''}
            </CardTitle>
            <CardDescription>
              {isOwner
                ? 'Você é o proprietário e pode gerenciar todos os usuários'
                : 'Você pode bloquear/desbloquear clientes e resetar suas senhas'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 text-center">
                <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b text-xs uppercase text-slate-500">
                      <th className="py-2 px-3">Nome / E-mail</th>
                      <th className="py-2 px-3">Função</th>
                      <th className="py-2 px-3">Status</th>
                      <th className="py-2 px-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody data-testid="users-list">
                    {users.map((u) => (
                      <tr key={u.id} className="border-b hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                        <td className="py-3 px-3">
                          <div className="font-medium text-slate-800">{u.name}</div>
                          <div className="text-xs text-slate-500">{u.email}</div>
                        </td>
                        <td className="py-3 px-3">
                          {u.is_owner ? (
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                              <Crown className="w-3 h-3 mr-1" /> Proprietário
                            </Badge>
                          ) : u.role === 'admin' ? (
                            <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100">
                              <ShieldCheck className="w-3 h-3 mr-1" /> Admin
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Cliente</Badge>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          {u.is_blocked ? (
                            <Badge variant="destructive" className="gap-1">
                              <Ban className="w-3 h-3" /> Bloqueado
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-700 border-emerald-200 gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Ativo
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex justify-end gap-1 flex-wrap">
                            {/* Reset Password */}
                            {!u.is_owner && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setResetTarget(u); setResetOpen(true); }}
                                title="Resetar senha"
                                data-testid={`reset-pass-${u.id}`}
                              >
                                <KeyRound className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Block/Unblock */}
                            {!u.is_owner && u.id !== currentUser?.id && (
                              u.is_blocked ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUnblock(u)}
                                  title="Desbloquear"
                                  data-testid={`unblock-${u.id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setBlockTarget(u); setBlockOpen(true); }}
                                  title="Bloquear"
                                  data-testid={`block-${u.id}`}
                                >
                                  <Ban className="w-4 h-4 text-amber-600" />
                                </Button>
                              )
                            )}
                            {/* Promote/Demote (owner only) */}
                            {isOwner && !u.is_owner && u.id !== currentUser?.id && (
                              u.role === 'admin' ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDemote(u)}
                                  title="Rebaixar a cliente"
                                  data-testid={`demote-${u.id}`}
                                >
                                  <ShieldOff className="w-4 h-4 text-slate-600" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handlePromote(u)}
                                  title="Promover a admin"
                                  data-testid={`promote-${u.id}`}
                                >
                                  <ShieldCheck className="w-4 h-4 text-indigo-600" />
                                </Button>
                              )
                            )}
                            {/* Delete (owner only) */}
                            {isOwner && !u.is_owner && u.id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(u)}
                                title="Excluir"
                                data-testid={`delete-${u.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr><td colSpan="4" className="text-center py-8 text-slate-400">Nenhum usuário encontrado</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resetar senha</DialogTitle>
            <DialogDescription>
              Defina uma nova senha para <strong>{resetTarget?.email}</strong>. O usuário poderá usar esta senha imediatamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Nova senha (mín. 6)</Label>
            <Input
              type="password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              data-testid="reset-password-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancelar</Button>
            <Button onClick={handleReset} data-testid="reset-password-submit">
              <Lock className="w-4 h-4 mr-2" /> Redefinir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Dialog */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bloquear usuário</DialogTitle>
            <DialogDescription>
              Vai bloquear <strong>{blockTarget?.email}</strong>. Ele(a) não poderá fazer login até ser desbloqueado(a).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivo (opcional)</Label>
            <Input
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Ex: comportamento suspeito"
              data-testid="block-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockOpen(false)}>Cancelar</Button>
            <Button onClick={handleBlock} variant="destructive" data-testid="block-submit">
              <Ban className="w-4 h-4 mr-2" /> Bloquear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUsers;
